﻿<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @mousedown.self="$emit('close')">
    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div
        class="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
        <div class="flex items-center gap-3">
          <span class="material-symbols-outlined text-2xl" :class="typeIconColor">
            {{ typeIcon }}
          </span>
          <div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">{{ form.name }}</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ handlerMeta?.name || automation.automationType }}</p>
          </div>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <div v-if="loading" class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>

        <!-- Basic Information -->
        <div v-else class="space-y-6">
          <div class="space-y-4">
            <h3 class="font-semibold text-gray-900 dark:text-white">
              {{ tt('Basic Information', 'Osnovni podatki') }}
            </h3>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {{ tt('Automation Name', 'Ime avtomatizacije') }} *
              </label>
              <input
                v-model="form.name"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>

            <div class="flex items-center gap-3">
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" v-model="form.enabled" class="sr-only peer" />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
              <span class="text-sm text-gray-700 dark:text-gray-300">{{
                tt('Enable automation', 'Omogoči avtomatizacijo')
              }}</span>
            </div>
          </div>

          <div
            v-if="handlerMeta?.supportsWebhookTrigger"
            class="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-3">
            <div class="flex items-start gap-3">
              <span class="material-symbols-outlined text-amber-600 dark:text-amber-400">webhook</span>
              <div class="text-sm text-amber-900 dark:text-amber-100 min-w-0">
                <p class="font-medium">{{ tt('Webhook URL', 'URL webhooka') }}</p>
                <p class="mt-1 text-amber-800 dark:text-amber-200 break-words">
                  {{
                    webhookUrl
                      ? webhookHint
                      : tt(
                          'Save this function once to generate its private webhook URL.',
                          'Shranite to funkcijo enkrat, da se ustvari njen zasebni URL webhooka.',
                        )
                  }}
                </p>
              </div>
            </div>

            <div v-if="webhookUrl" class="flex flex-col gap-2 sm:flex-row">
              <input
                :value="webhookUrl"
                type="text"
                readonly
                class="flex-1 px-3 py-2 text-sm border border-amber-200 dark:border-amber-700 rounded-lg bg-white/90 dark:bg-gray-900/40 text-gray-900 dark:text-white" />
              <button
                @click="copyWebhookUrl"
                type="button"
                class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2">
                <span class="material-symbols-outlined text-sm">
                  {{
                    webhookCopyStatus === 'copied' ? 'check' : webhookCopyStatus === 'error' ? 'error' : 'content_copy'
                  }}
                </span>
                {{
                  webhookCopyStatus === 'copied'
                    ? tt('Copied', 'Kopirano')
                    : webhookCopyStatus === 'error'
                      ? tt('Copy Failed', 'Kopiranje ni uspelo')
                      : tt('Copy URL', 'Kopiraj URL')
                }}
              </button>
            </div>

            <p v-if="webhookUrl" class="text-xs text-amber-800 dark:text-amber-200">
              {{
                tt(
                  'Treat this URL as a secret. Methods: GET or POST',
                  'S tem URL ravnajte kot s skrivnostjo. Metode: GET ali POST',
                )
              }}
            </p>
          </div>

          <!-- Info Card -->
          <div class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div class="flex items-start gap-3">
              <span class="material-symbols-outlined text-blue-600 dark:text-blue-400">info</span>
              <div class="text-sm text-blue-800 dark:text-blue-200">
                <p class="font-medium">{{ handlerMeta?.name }}</p>
                <p class="mt-1">{{ handlerMeta?.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Device Bindings -->
        <div class="space-y-6">
          <h3 class="font-semibold text-gray-900 dark:text-white">{{ tt('Device Bindings', 'Povezave naprav') }}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{
              tt(
                'Connect device properties to this automation pattern.',
                'Povežite lastnosti naprav s tem vzorcem avtomatizacije.',
              )
            }}
          </p>

          <!-- Required Bindings -->
          <div v-if="handlerMeta?.requiredBindings?.length" class="space-y-4">
            <h3 class="font-medium text-gray-900 dark:text-white">
              {{ tt('Required Bindings', 'Zahtevane povezave') }}
            </h3>
            <template v-for="role in handlerMeta.requiredBindings" :key="role">
              <!-- Multi-binding role (zones) -->
              <div v-if="role === 'zone'" class="space-y-3">
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {{ formatBindingRolePlural(role) }} *
                  </label>
                  <button
                    @click="addZone"
                    type="button"
                    class="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1">
                    <span class="material-symbols-outlined text-sm">add</span>
                    {{ tt('Add Zone', 'Dodaj cono') }}
                  </button>
                </div>
                <div v-for="(zone, index) in zoneBindings" :key="index" class="flex items-center gap-2">
                  <span class="text-sm text-gray-500 dark:text-gray-400 w-8">{{ index + 1 }}.</span>
                  <div class="flex-1">
                    <DeviceValuePicker
                      v-model="zoneBindings[index]"
                      :title="`${tt('Select Zone', 'Izberi cono')} ${index + 1}`"
                      :filter-device-id="isLocalOnly('zone') ? props.automation.deviceId : undefined" />
                  </div>
                  <button
                    v-if="zoneBindings.length > 1"
                    @click="removeZone(index)"
                    type="button"
                    class="p-1 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded">
                    <span class="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{
                    tt(
                      'Zones will be watered sequentially in the order listed above.',
                      'Cone se zalivajo zaporedoma v zgoraj navedenem vrstnem redu.',
                    )
                  }}
                </p>
              </div>

              <!-- Single binding role -->
              <div v-else class="space-y-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ formatBindingRole(role) }} *
                </label>
                <DeviceValuePicker
                  v-model="bindings[role]"
                  :title="`${tt('Select', 'Izberi')} ${formatBindingRole(role)}`"
                  :filter-device-id="isLocalOnly(role) ? props.automation.deviceId : undefined" />
              </div>
            </template>
          </div>

          <!-- Optional Bindings -->
          <div v-if="handlerMeta?.optionalBindings?.length" class="space-y-4">
            <h3 class="font-medium text-gray-900 dark:text-white">
              {{ tt('Optional Bindings', 'Neobvezne povezave') }}
            </h3>
            <div v-for="role in handlerMeta.optionalBindings" :key="role" class="space-y-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ formatBindingRole(role) }}
              </label>
              <DeviceValuePicker
                v-model="bindings[role]"
                :title="`${tt('Select', 'Izberi')} ${formatBindingRole(role)}`"
                :filter-device-id="isLocalOnly(role) ? props.automation.deviceId : undefined" />
              <p v-if="bindingDescription(role)" class="text-xs text-gray-500 dark:text-gray-400">
                {{ bindingDescription(role) }}
              </p>
              <button
                v-if="bindings[role]"
                @click="bindings[role] = null"
                class="text-xs text-red-600 dark:text-red-400 hover:underline">
                {{ tt('Remove binding', 'Odstrani povezavo') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Configuration -->
        <div v-if="handlerMeta?.configSchema?.length" class="space-y-6">
          <h3 class="font-semibold text-gray-900 dark:text-white">{{ tt('Configuration', 'Konfiguracija') }}</h3>

          <div class="space-y-4">
            <SunTrackerEditor v-if="isSunTracking" v-model="form.config" />

            <FanTempControllerEditor v-else-if="isFanTempController" v-model="form.config" />

            <FanPresenceTimerEditor v-else-if="isFanPresenceTimer" v-model="form.config" />

            <FanExternalControlEditor v-else-if="isFanExternalControl" v-model="form.config" />

            <CoverExternalControlEditor v-else-if="isCoverExternalControl" v-model="form.config" />

            <TimeSwitchScheduleEditor
              v-else-if="isTimeSwitch"
              :model-value="form.config"
              @update:model-value="form.config = $event" />

            <div v-for="field in displayedConfigFields" :key="field.key" class="space-y-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ field.label }}
                <span v-if="field.required" class="text-red-500">*</span>
                <span v-if="field.unit" class="text-gray-400 font-normal">({{ field.unit }})</span>
              </label>

              <!-- Number input -->
              <div v-if="field.type === 'number'" class="flex items-center gap-2">
                <input
                  v-model.number="form.config[field.key]"
                  type="number"
                  :min="field.min"
                  :max="field.max"
                  :step="field.step || 1"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <!-- Boolean input -->
              <div v-else-if="field.type === 'boolean'" class="flex items-center">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" v-model="form.config[field.key]" class="sr-only peer" />
                  <div
                    class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <!-- Select input -->
              <select
                v-else-if="field.type === 'select'"
                v-model="form.config[field.key]"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option
                  v-for="opt in field.options"
                  :key="typeof opt === 'object' ? opt.value : opt"
                  :value="typeof opt === 'object' ? opt.value : opt">
                  {{ typeof opt === 'object' ? opt.label : formatOption(opt) }}
                </option>
              </select>

              <!-- Text/Time input -->
              <input
                v-else
                v-model="form.config[field.key]"
                :type="field.type === 'time' ? 'time' : 'text'"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />

              <p v-if="field.description" class="text-xs text-gray-500 dark:text-gray-400">
                {{ field.description }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
        <button
          @click="confirmDelete"
          class="px-4 py-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg transition-colors">
          {{ tt('Delete', 'Izbriši') }}
        </button>

        <div class="flex items-center gap-3">
          <button
            @click="$emit('close')"
            class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            {{ tt('Cancel', 'Prekliči') }}
          </button>

          <button
            @click="save"
            :disabled="saving || !isValid"
            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors">
            {{ saving ? tt('Saving...', 'Shranjevanje...') : tt('Save Changes', 'Shrani spremembe') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { apiFetch, getWebhookBaseUrl } from '../services/api';
import type { DeviceAutomationBinding } from '../stores/deviceAutomations';
import { getTimeSwitchScheduleErrors } from '../utils/switchAutomation';
import { getSunTrackerErrors, normalizeSunTrackerConfig } from '../utils/sunTracker';
import { useShadeSeasonLabels } from '../composables/useShadeSeasonLabels';
import {
  getFanExternalControlErrors,
  getFanPresenceTimerErrors,
  getFanTempControllerErrors,
  normalizeFanExternalControlConfig,
  normalizeFanPresenceTimerConfig,
  normalizeFanTempControllerConfig,
} from '../utils/fanAutomation';
import { getCoverExternalControlErrors, normalizeCoverExternalControlConfig } from '../utils/coverAutomation';
import DeviceValuePicker from './DeviceValuePicker.vue';
import TimeSwitchScheduleEditor from './devices/TimeSwitchScheduleEditor.vue';
import SunTrackerEditor from './devices/sun/SunTrackerEditor.vue';
import FanTempControllerEditor from './devices/fan/FanTempControllerEditor.vue';
import FanPresenceTimerEditor from './devices/fan/FanPresenceTimerEditor.vue';
import FanExternalControlEditor from './devices/fan/FanExternalControlEditor.vue';
import CoverExternalControlEditor from './devices/cover/CoverExternalControlEditor.vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '../composables/useToast';
import { useMessageBox } from '../composables/useMessageBox';
import { useDeviceAutomationsStore } from '../stores/deviceAutomations';
import { tt } from '../utils/consoleText';
import type { Bilingual } from '../config/bilingual';

const { t } = useI18n();
const toast = useToast();
const { confirm: confirmBox } = useMessageBox();
const deviceAutomationsStore = useDeviceAutomationsStore();
const sunSeasonLabels = useShadeSeasonLabels();

interface DeviceAutomation {
  id: string;
  name: string;
  automationType: string;
  enabled: boolean;
  config: Record<string, unknown>;
  bindings?: DeviceAutomationBinding[];
  deviceId?: string;
}

interface HandlerMeta {
  automationType: string;
  name: string;
  description: string;
  requiredBindings: string[];
  optionalBindings: string[];
  bindingMetadata?: Array<{ role: string; localOnly?: boolean; description?: string }>;
  configSchema: ConfigField[];
  supportsWebhookTrigger?: boolean;
}

interface ConfigField {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'select' | 'text' | 'time';
  default?: unknown;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: Array<string | { value: string; label: string }>;
  description?: string;
  required?: boolean;
}

const props = defineProps<{
  automation: DeviceAutomation;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved'): void;
  (e: 'deleted'): void;
}>();

const timeSwitchConfigKeys = new Set([
  'schedule_mode',
  'daily_on_time',
  'daily_off_time',
  'daily_schedule',
  'weekly_schedule',
]);
const loading = ref(true);
const saving = ref(false);
const handlerMeta = ref<HandlerMeta | null>(null);
const webhookCopyStatus = ref<'idle' | 'copied' | 'error'>('idle');
const bindings = reactive<Record<string, { valueId: string; deviceName?: string; propertyName?: string } | null>>({});
const zoneBindings = ref<Array<{ valueId: string; deviceName?: string; propertyName?: string } | null>>([]);

const form = reactive({
  name: props.automation.name,
  enabled: props.automation.enabled,
  config: { ...props.automation.config },
});

const webhookId = computed(() => {
  const value = form.config?.hook_id;
  return typeof value === 'string' ? value.trim() : '';
});

/** What the inbound hook does, in this automation type's own words. */
const webhookHint = computed(() => {
  if (props.automation.automationType === 'light_motion_switch') {
    return tt(
      'GET or POST this URL to report motion. An empty body switches the light on and starts the off delay, {"state":false} reports that motion ended, and {"lux":12.5} supplies a brightness reading for the darkness gate.',
      'Na ta URL pošljite GET ali POST za sporočilo o gibanju. Prazno telo vklopi luč in zažene zakasnitev izklopa, {"state":false} sporoči konec gibanja, {"lux":12.5} pa posreduje meritev svetlosti za prag teme.',
    );
  }
  return tt(
    'This backend URL supports both GET and POST. For automations using "From Payload", use POST JSON or add a query like ?action=lock.',
    'Ta URL vmesnika podpira GET in POST. Za avtomatizacije, ki uporabljajo »From Payload«, uporabite POST JSON ali dodajte poizvedbo, kot je ?action=lock.',
  );
});

const webhookUrl = computed(() => {
  if (!webhookId.value) return '';
  return `${getWebhookBaseUrl()}/${webhookId.value}`;
});

const addZone = () => {
  zoneBindings.value.push(null);
};

const removeZone = (index: number) => {
  if (zoneBindings.value.length > 1) {
    zoneBindings.value.splice(index, 1);
  }
};

const typeIcons: Record<string, { icon: string; color: string }> = {
  light_motion_switch: { icon: 'motion_sensor_active', color: 'text-yellow-500' },
  light_brightness_switch: { icon: 'light_mode', color: 'text-orange-500' },
  light_external_switch: { icon: 'toggle_on', color: 'text-blue-500' },
  analog_threshold_alarm: { icon: 'warning', color: 'text-red-500' },
  shade_wind_protection: { icon: 'air', color: 'text-cyan-500' },
  shade_rain_protection: { icon: 'water_drop', color: 'text-blue-400' },
  shade_sun_tracking: { icon: 'wb_sunny', color: 'text-amber-500' },
  fan_temperature_controller: { icon: 'thermostat', color: 'text-sky-500' },
  fan_presence_timer: { icon: 'mode_fan', color: 'text-sky-500' },
  fan_external_control: { icon: 'switch', color: 'text-sky-500' },
  cover_external_control: { icon: 'switch', color: 'text-green-500' },
  irrigation_controller: { icon: 'water', color: 'text-green-500' },
  irrigation_rain_skip: { icon: 'water_drop', color: 'text-teal-500' },
};

const typeIcon = computed(() => typeIcons[props.automation.automationType]?.icon || 'settings');
const typeIconColor = computed(() => typeIcons[props.automation.automationType]?.color || 'text-gray-500');
const isTimeSwitch = computed(() => props.automation.automationType === 'time_switch');
const isSunTracking = computed(() => props.automation.automationType === 'shade_sun_tracking');
const isFanTempController = computed(() => props.automation.automationType === 'fan_temperature_controller');
const isFanPresenceTimer = computed(() => props.automation.automationType === 'fan_presence_timer');
const isFanExternalControl = computed(() => props.automation.automationType === 'fan_external_control');
const isCoverExternalControl = computed(() => props.automation.automationType === 'cover_external_control');
const isFanType = computed(
  () =>
    isFanTempController.value || isFanPresenceTimer.value || isFanExternalControl.value || isCoverExternalControl.value,
);
const timeSwitchScheduleErrors = computed(() => (isTimeSwitch.value ? getTimeSwitchScheduleErrors(form.config) : []));
const sunTrackerErrors = computed(() =>
  isSunTracking.value ? getSunTrackerErrors(form.config, sunSeasonLabels.value) : [],
);
const fanAutomationErrors = computed(() => {
  if (isFanTempController.value) return getFanTempControllerErrors(form.config);
  if (isFanPresenceTimer.value) return getFanPresenceTimerErrors(form.config);
  if (isFanExternalControl.value) return getFanExternalControlErrors(form.config);
  if (isCoverExternalControl.value) return getCoverExternalControlErrors(form.config);
  return [];
});
const displayedConfigFields = computed(() => {
  if (!handlerMeta.value?.configSchema) return [];
  if (isSunTracking.value || isFanType.value) return [];
  // `hook_id` is the generated webhook credential — the amber card shows it as
  // a copyable URL, never as an editable field.
  const fields = handlerMeta.value.configSchema.filter((field) => field.key !== 'hook_id');
  if (!isTimeSwitch.value) return fields;
  return fields.filter((field) => !timeSwitchConfigKeys.has(field.key));
});

/**
 * The binding-role vocabulary, in both languages (same wording as the add modal's
 * table — the two modals edit the same automations). `tt()` picks the half.
 */
const BINDING_ROLE_LABELS: Record<string, Bilingual> = {
  trigger: { en: 'Trigger Input', sl: 'Vhod sprožilca' },
  output: { en: 'Output Control', sl: 'Krmiljenje izhoda' },
  output_tilt: { en: 'Tilt Control', sl: 'Krmiljenje nagiba' },
  brightness: { en: 'Light Sensor', sl: 'Senzor svetlosti' },
  presence: { en: 'Presence Sensor', sl: 'Senzor prisotnosti' },
  temperature: { en: 'Temperature Sensor', sl: 'Temperaturni senzor' },
  humidity: { en: 'Humidity Sensor', sl: 'Senzor vlage' },
  speed_up: { en: 'Speed Up Button', sl: 'Tipka za hitreje' },
  speed_down: { en: 'Speed Down Button', sl: 'Tipka za počasneje' },
  open_button: { en: 'Open Button', sl: 'Tipka za odpiranje' },
  close_button: { en: 'Close Button', sl: 'Tipka za zapiranje' },
  stop_button: { en: 'Stop Button', sl: 'Tipka za ustavitev' },
  output_stop: { en: 'Cover Stop', sl: 'Ustavitev senčil' },
  condition: { en: 'Condition Input', sl: 'Vhod pogoja' },
  inhibit: { en: 'Inhibit Signal', sl: 'Signal blokade' },
  demand: { en: 'Water Demand', sl: 'Poraba vode' },
  leak: { en: 'Leak Sensor', sl: 'Senzor puščanja' },
  level: { en: 'Tank Level', sl: 'Nivo rezervoarja' },
  ph: { en: 'pH Reading', sl: 'Meritev pH' },
  orp: { en: 'ORP / Redox', sl: 'ORP / redoks' },
  salinity: { en: 'Salinity', sl: 'Slanost' },
  door: { en: 'Door Contact', sl: 'Kontakt vrat' },
  zone: { en: 'Irrigation Zone', sl: 'Namakalna cona' },
};

/** The plural of a multi-binding role — the zone list's own heading. */
const BINDING_ROLE_PLURALS: Record<string, Bilingual> = {
  zone: { en: 'Irrigation Zones', sl: 'Namakalne cone' },
};

const formatBindingRole = (role: string) => {
  const label = BINDING_ROLE_LABELS[role];
  return label ? tt(label.en, label.sl) : role;
};

const formatBindingRolePlural = (role: string) => {
  const label = BINDING_ROLE_PLURALS[role];
  return label ? tt(label.en, label.sl) : `${formatBindingRole(role)}s`;
};

const formatOption = (opt: string) => {
  return opt.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Check if a binding role is local-only (output must be on parent device) */
const isLocalOnly = (role: string): boolean => {
  return handlerMeta.value?.bindingMetadata?.some((m) => m.role === role && m.localOnly) ?? false;
};

/** The handler's own wording for a binding role, when it carries one. */
const bindingDescription = (role: string): string => {
  return handlerMeta.value?.bindingMetadata?.find((m) => m.role === role)?.description ?? '';
};

const isValid = computed(() => {
  if (!form.name.trim()) return false;
  if (!handlerMeta.value) return false;

  // Check required bindings
  for (const role of handlerMeta.value.requiredBindings) {
    if (role === 'zone') {
      // Need at least one zone for irrigation
      if (!zoneBindings.value.some((z) => z?.valueId)) return false;
    } else {
      if (!bindings[role]?.valueId) return false;
    }
  }

  if (isTimeSwitch.value && timeSwitchScheduleErrors.value.length > 0) {
    return false;
  }

  if (isSunTracking.value && sunTrackerErrors.value.length > 0) {
    return false;
  }

  if (isFanType.value && fanAutomationErrors.value.length > 0) {
    return false;
  }

  return true;
});

const copyWebhookUrl = async () => {
  if (!webhookUrl.value) return;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(webhookUrl.value);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = webhookUrl.value;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'absolute';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    webhookCopyStatus.value = 'copied';
  } catch (err) {
    console.error('Failed to copy webhook URL', err);
    webhookCopyStatus.value = 'error';
  }

  window.setTimeout(() => {
    webhookCopyStatus.value = 'idle';
  }, 2000);
};

const fetchData = async () => {
  try {
    loading.value = true;

    // Fetch handler metadata
    const types = await apiFetch<HandlerMeta[]>('/device-automations/meta/types');
    handlerMeta.value = types.find((t) => t.automationType === props.automation.automationType) ?? null;

    // Fetch full automation with bindings
    const data = await apiFetch(`/device-automations/${props.automation.id}`);

    form.name = data.name;
    form.enabled = data.enabled;
    form.config = { ...(data.config || {}) };

    // Populate bindings
    if (data.bindings) {
      for (const binding of data.bindings) {
        if (binding.bindingRole === 'zone') {
          // Add to zone bindings array
          zoneBindings.value.push({
            valueId: binding.valueId,
            deviceName: binding.deviceName,
            propertyName: binding.valueName || binding.valueKey,
          });
        } else {
          bindings[binding.bindingRole] = {
            valueId: binding.valueId,
            deviceName: binding.deviceName,
            propertyName: binding.valueName || binding.valueKey,
          };
        }
      }
    }

    // Ensure at least one zone slot exists for irrigation type
    if (handlerMeta.value?.requiredBindings.includes('zone') && zoneBindings.value.length === 0) {
      zoneBindings.value.push(null);
    }

    // Populate config with defaults for missing keys
    if (handlerMeta.value?.configSchema) {
      for (const field of handlerMeta.value.configSchema) {
        if (form.config[field.key] === undefined && field.default !== undefined) {
          form.config[field.key] = field.default;
        }
      }
    }

    // Advanced sun controller: normalize into the full nested structure
    if (isSunTracking.value) {
      form.config = normalizeSunTrackerConfig(form.config) as unknown as Record<string, unknown>;
    }

    // Fan automations: normalize into the full nested structure
    if (isFanTempController.value) {
      form.config = normalizeFanTempControllerConfig(form.config) as unknown as Record<string, unknown>;
    }
    if (isFanPresenceTimer.value) {
      form.config = normalizeFanPresenceTimerConfig(form.config) as unknown as Record<string, unknown>;
    }
    if (isFanExternalControl.value) {
      form.config = normalizeFanExternalControlConfig(form.config) as unknown as Record<string, unknown>;
    }
    if (isCoverExternalControl.value) {
      form.config = normalizeCoverExternalControlConfig(form.config) as unknown as Record<string, unknown>;
    }
  } catch (err) {
    console.error('Failed to load automation data', err);
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  if (!isValid.value || !handlerMeta.value) return;

  try {
    saving.value = true;

    // Build bindings array
    const bindingsArray = [];
    const allRoles = [...handlerMeta.value.requiredBindings, ...handlerMeta.value.optionalBindings];

    for (const role of allRoles) {
      if (role === 'zone') {
        // Add all zone bindings
        for (const zone of zoneBindings.value) {
          if (zone?.valueId) {
            bindingsArray.push({
              bindingRole: 'zone',
              valueId: zone.valueId,
            });
          }
        }
      } else {
        const binding = bindings[role];
        if (binding?.valueId) {
          bindingsArray.push({
            bindingRole: role,
            valueId: binding.valueId,
          });
        }
      }
    }

    await apiFetch(`/device-automations/${props.automation.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: form.name.trim(),
        enabled: form.enabled,
        config: form.config,
        bindings: bindingsArray,
      }),
    });

    deviceAutomationsStore.notifyAutomationsMutated();
    emit('saved');
  } catch (err) {
    console.error('Failed to save automation', err);
    toast.error(t('dialogs.automationSaveFailed'));
  } finally {
    saving.value = false;
  }
};

const confirmDelete = async () => {
  const ok = await confirmBox(t('dialogs.automationDeleteConfirm', { name: props.automation.name }));
  if (!ok) {
    return;
  }

  try {
    await apiFetch(`/device-automations/${props.automation.id}`, {
      method: 'DELETE',
    });
    deviceAutomationsStore.notifyAutomationsMutated();
    emit('deleted');
  } catch (err) {
    console.error('Failed to delete automation', err);
    toast.error(t('dialogs.automationDeleteFailed'));
  }
};

onMounted(() => {
  fetchData();
});
</script>
